import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromLink = searchParams.get('token') || '';
  const [token, setToken] = useState(tokenFromLink);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error('Återställningstoken saknas'); return; }
    if (newPassword.length < 6) { toast.error('Lösenordet måste vara minst 6 tecken'); return; }
    if (newPassword !== confirmPassword) { toast.error('Lösenorden matchar inte'); return; }

    setLoading(true);
    try {
      await authApi.resetPassword(token, newPassword);
      setSuccess(true);
      toast.success('Lösenordet har återställts!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte återställa lösenordet. Token kan ha gått ut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-1 mb-6">
            <span className="text-3xl font-black text-[#003B73]">Bygg</span>
            <span className="text-3xl font-black text-[#FF8A00]">Fynd</span>
          </Link>
          <h1 className="text-2xl font-black text-[#0F172A]">Återställ lösenord</h1>
          <p className="text-gray-500 mt-2">
            {success ? 'Ditt lösenord har uppdaterats.' : 'Ange ditt nya lösenord nedan.'}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              {!tokenFromLink ? (
                <div>
                  <Label htmlFor="reset-token">Återställningstoken</Label>
                  <Input
                    id="reset-token"
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    required
                    className="mt-1 h-11 rounded-xl font-mono text-xs"
                    placeholder="Klistra in länken eller token från mailet"
                    data-testid="reset-token-input"
                  />
                </div>
              ) : null}

              <div>
                <Label htmlFor="new-password">Nytt lösenord</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-10 h-11 rounded-xl"
                    placeholder="Minst 6 tecken"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-password">Bekräfta lösenord</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="mt-1 h-11 rounded-xl"
                  placeholder="Ange lösenordet igen"
                  data-testid="confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#003B73] hover:bg-[#002a52] font-bold rounded-xl"
                data-testid="reset-submit-btn"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Återställer...</>
                ) : (
                  'Återställ lösenord'
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-5 text-center">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                <p className="text-sm text-emerald-700 text-left">
                  Ditt lösenord har ändrats. Du kan nu logga in med ditt nya lösenord.
                </p>
              </div>
              <Button
                onClick={() => navigate('/logga-in')}
                className="w-full h-12 bg-[#FF8A00] hover:bg-[#e67c00] font-bold rounded-xl"
                data-testid="go-to-login-btn"
              >
                Gå till inloggning
              </Button>
            </div>
          )}

          <div className="mt-6 text-center">
            <Link
              to="/logga-in"
              className="inline-flex items-center gap-1 text-sm text-[#003B73] hover:underline font-medium"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Tillbaka till inloggning
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
